export interface FgdsProductClassificationInput {
  materialNumber?: string | number;
  type?: string | null;
  subType?: string | null;
  fullType?: string | null;
}

export interface FgdsProductClassificationMapping {
  readonly materialNumbers: readonly string[];
  readonly legacyType: string;
  readonly legacySubType?: string;
  readonly normalizedType: string;
}

/**
 * Compatibility mappings for legacy FGDS responses. New canonical types must not be added here;
 * values that are not legacy combinations pass through unchanged.
 */
export const FGDS_REGARD_3000_PLUS_CLASSIFICATION_MAPPINGS: readonly FgdsProductClassificationMapping[] = [
  {
    materialNumbers: ["3734916"],
    legacyType: "gwz",
    legacySubType: "3000plus-base",
    normalizedType: "gwz-base",
  },
  {
    materialNumbers: ["3738952"],
    legacyType: "gwz",
    legacySubType: "3000plus-base",
    normalizedType: "z-o-gwz-ds",
  },
  {
    materialNumbers: ["3715011", "3738916"],
    legacyType: "gwz",
    legacySubType: "3000plus-lid",
    normalizedType: "z-n-gwz-db",
  },
  {
    materialNumbers: ["3705680", "3705681", "3705682"],
    legacyType: "z-o",
    legacySubType: "3000plus-input",
    normalizedType: "z-o-gwz-input-analog",
  },
  {
    materialNumbers: ["3705687", "3705688"],
    legacyType: "z-o",
    legacySubType: "3000plus-relais",
    normalizedType: "z-o-gwz-relais",
  },
  {
    materialNumbers: ["3705693", "3705694"],
    legacyType: "z-o",
    legacySubType: "3000plus-gateway",
    normalizedType: "z-o-gwz-gateway",
  },
  {
    materialNumbers: ["3705672"],
    legacyType: "z-o",
    legacySubType: "3000plus-slotcover",
    normalizedType: "z-o-slotcover",
  },
  {
    materialNumbers: ["3707819", "8327253", "3720165", "3709674", "3716411", "3711953", "3704261"],
    legacyType: "z-o",
    normalizedType: "z-o",
  },
] as const;

const LEGACY_CLASSIFICATION_TYPES = new Map(
  [...FGDS_REGARD_3000_PLUS_CLASSIFICATION_MAPPINGS]
    .reverse()
    .filter((mapping) => mapping.legacySubType)
    .map((mapping) => [`${mapping.legacyType}\u0000${mapping.legacySubType}`, mapping.normalizedType]),
);

export const FGDS_REGARD_3000_PLUS_MATERIAL_TYPE_MAP: ReadonlyMap<string, string> = new Map(
  FGDS_REGARD_3000_PLUS_CLASSIFICATION_MAPPINGS.flatMap((mapping) =>
    mapping.materialNumbers.map((materialNumber) => [materialNumber, mapping.normalizedType] as const),
  ),
);

/**
 * Resolves the effective product-level FGDS type without changing the input.
 * Canonical API types are authoritative; known legacy type/subtype pairs are translated as a fallback.
 */
export function resolveFgdsProductType(input: FgdsProductClassificationInput): string | undefined {
  const type = typeof input.type === "string" ? input.type : undefined;
  const materialNumber = input.materialNumber === undefined ? undefined : String(input.materialNumber);
  const materialClassification = materialNumber ? FGDS_REGARD_3000_PLUS_MATERIAL_TYPE_MAP.get(materialNumber) : undefined;
  if (materialClassification && (type === "gwz" || type === "z-o")) {
    return materialClassification;
  }

  const legacyType = type;
  const legacySubType = typeof input.subType === "string" ? input.subType : undefined;
  const legacyClassification =
    legacyType && legacySubType ? LEGACY_CLASSIFICATION_TYPES.get(`${legacyType}\u0000${legacySubType}`) : undefined;
  if (legacyClassification) {
    return legacyClassification;
  }

  const fullType = typeof input.fullType === "string" ? input.fullType : undefined;
  if (fullType && (legacyType === "gwz" || legacyType === "z-o")) {
    if (fullType.substring(0, 4) === "gwz-" || fullType.substring(0, 4) === "z-o-" || fullType.substring(0, 4) === "z-n-") {
      return fullType;
    }
  }

  return type;
}
